import { useCreateRoute } from 'components/create-dialog/CreateDialogContext';
import { draftIdIn } from 'components/create-dialog/useCreateDraft';
import Loader from 'components/Loader';
import { useMemo, useState } from 'react';
import { useGetJobRoleByIdQuery } from 'store/api/jobRoles';
import { JobRoleDetailsDto, JobRoleForm } from 'types/jobRoles';
import { useClassifiers } from 'utils/useClassifiers';
import { jobRoleDtoToForm } from './jobRoleFormMapping';
import JobRolePageForm from './JobRolePageForm';

const USED_CLASSIFIERS = ['JOB_ROLE_GROUP_TYPE', 'LANGUAGE'];

/** The add form loaded with what a draft holds, read as the job role's edit page reads it. */
const JobRoleDraftAdd = ({ draftId }: { draftId: number }) => {
    const [draft, setDraft] = useState<{ initialData: JobRoleForm; jobRole: JobRoleDetailsDto }>();
    const { data: jobRole } = useGetJobRoleByIdQuery(String(draftId), { refetchOnMountOrArgChange: true });
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: USED_CLASSIFIERS });

    // Frozen once loaded: a refetch must not reset what the user is typing.
    if (!draft && jobRole && !isLoadingClassifiers) {
        setDraft({ initialData: jobRoleDtoToForm(jobRole, classifiers), jobRole });
    }

    return draft ? (
        <JobRolePageForm
            type="add"
            initialData={draft.initialData}
            classifiers={classifiers}
            entityCustomFields={draft.jobRole.customFields}
        />
    ) : (
        <Loader />
    );
};

const JobRoleBlankAdd = () => {
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: USED_CLASSIFIERS,
    });

    // Derive initial form data from classifiers during render (no effect needed).
    const initialData = useMemo<JobRoleForm | undefined>(() => {
        if (!classifiers) return undefined;
        const supportedLanguages = Array.from(
            new Set(
                classifiers['LANGUAGE']?.values
                    ?.map((value) => value.code)
                    .toSorted((a: string, b: string) => a.localeCompare(b)) ?? []
            )
        );

        return {
            name: '',
            internalName: '',
            abbreviation: '',
            // Left empty: the server refuses 0, a draft's untouched order number included.
            orderNum: null,
            groupNameType: null,
            isActive: true,
            excludeFromMinmaxCalculation: false,
            entityTexts: supportedLanguages.map((langCode) => ({
                id: null,
                langCode,
                value: '',
                description: '',
                textMale: '',
                textFemale: '',
            })),
            jobRoleEmployees: [],
        };
    }, [classifiers]);

    if (isLoadingClassifiers || initialData === undefined) {
        return <Loader />;
    }

    return <JobRolePageForm type="add" initialData={initialData} classifiers={classifiers} />;
};

/** The job role create; its URL with `?draft=<id>` edits that draft instead of a blank job role. */
const JobRolePageAdd = () => {
    const draftId = draftIdIn(useCreateRoute().location.search);
    return draftId === null ? <JobRoleBlankAdd /> : <JobRoleDraftAdd key={draftId} draftId={draftId} />;
};

export default JobRolePageAdd;
